<template>
    <div class="p-4">
        <!-- Navegador de fecha -->
        <div class="card mb-3">
            <div class="card-body py-2 d-flex align-items-center justify-content-between gap-2">
                <button class="btn btn-sm btn-outline-secondary" @click="diaAnterior">
                    <i class="bi bi-chevron-left" />
                </button>
                <span class="fw-semibold small text-capitalize">{{ fechaFormateada }}</span>
                <div class="d-flex gap-1">
                    <button class="btn btn-sm btn-outline-secondary" @click="irAHoy" :disabled="esHoy">
                        Hoy
                    </button>
                    <button class="btn btn-sm btn-outline-secondary" @click="diaSiguiente">
                        <i class="bi bi-chevron-right" />
                    </button>
                </div>
            </div>
        </div>

        <!-- Lista -->
        <div class="card">
            <div class="card-body p-0">
                <!-- Loading -->
                <div v-if="loading" class="text-center py-5">
                    <div class="spinner-border spinner-border-sm text-secondary" role="status">
                        <span class="visually-hidden">Cargando...</span>
                    </div>
                </div>

                <!-- Error -->
                <div v-else-if="error" class="alert alert-danger m-3">
                    {{ error }}
                </div>

                <!-- Empty -->
                <div v-else-if="resultado && resultado.items.length === 0" class="text-center text-muted py-5 small">
                    No hay citas para este día.
                </div>

                <!-- Cards -->
                <template v-else-if="resultado">
                    <div
                        v-for="cita in resultado.items"
                        :key="cita.id"
                        class="border-bottom px-3 py-3"
                        :class="{ 'opacity-50': cita.realizada }"
                    >
                        <div class="d-flex justify-content-between align-items-start mb-1">
                            <span class="fw-semibold small" :class="{ 'text-decoration-line-through text-muted': cita.realizada }">{{ cita.asunto ?? '(Sin asunto)' }}</span>
                            <div class="d-flex align-items-center gap-2 ms-2 flex-shrink-0">
                                <span class="text-muted small text-nowrap">
                                    {{ formatHora(cita.fechaInicio) }} – {{ formatHora(cita.fechaFinal) }}
                                </span>
                                <button
                                    class="btn btn-sm p-0 border-0 lh-1"
                                    :class="cita.realizada ? 'text-success' : 'text-secondary'"
                                    :title="cita.realizada ? 'Marcar como pendiente' : 'Marcar como realizada'"
                                    :disabled="toggling.has(cita.id)"
                                    @click="toggleRealizada(cita)"
                                >
                                    <i class="bi" :class="cita.realizada ? 'bi-check-circle-fill' : 'bi-check-circle'" style="font-size: 1.1rem;" />
                                </button>
                            </div>
                        </div>
                        <div v-if="cita.aviso" class="small text-muted">
                            <RouterLink :to="{ name: 'aviso-detail', params: { idAviso: cita.aviso.idAviso } }" class="text-decoration-none">
                                <i class="bi bi-bell me-1" />Aviso #{{ cita.aviso.idAviso }}
                            </RouterLink>
                        </div>
                        <div v-if="cita.cliente" class="small text-muted">
                            <RouterLink :to="{ name: 'cliente-detail', params: { idc: cita.cliente.idc } }" class="text-decoration-none">
                                <i class="bi bi-building me-1" />{{ cita.cliente.nombreFiscal }}
                            </RouterLink>
                        </div>
                        <div v-if="cita.observaciones" class="small text-muted mt-1">
                            {{ cita.observaciones }}
                        </div>
                    </div>

                    <!-- Paginación (solo si hay más de una página) -->
                    <div v-if="resultado.totalPages > 1" class="d-flex align-items-center justify-content-between px-3 py-2 border-top">
                        <span class="small text-muted">
                            {{ resultado.totalCount }} registros &middot; Página {{ resultado.page }} de {{ resultado.totalPages }}
                        </span>
                        <div class="d-flex gap-1">
                            <button
                                class="btn btn-sm btn-outline-secondary"
                                :disabled="resultado.page <= 1"
                                @click="cambiarPagina(resultado.page - 1)"
                            >
                                <i class="bi bi-chevron-left" />
                            </button>
                            <button
                                class="btn btn-sm btn-outline-secondary"
                                :disabled="resultado.page >= resultado.totalPages"
                                @click="cambiarPagina(resultado.page + 1)"
                            >
                                <i class="bi bi-chevron-right" />
                            </button>
                        </div>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { usePageHeader } from '@/composables/usePageHeader'
import { useBreadcrumbs } from '@/composables/useBreadcrumbs'
import { miAgendaApi } from '@/api/miAgendaApi'
import type { Agenda, PaginatedResponse } from '@/types/agenda'

usePageHeader('Mi agenda')
useBreadcrumbs([{ label: 'Mi agenda' }])

const PAGE_SIZE = 50

const route = useRoute()
const router = useRouter()

const loading = ref(false)
const error = ref<string | null>(null)
const resultado = ref<PaginatedResponse<Agenda> | null>(null)
const toggling = ref(new Set<string>())

async function toggleRealizada(cita: Agenda) {
    if (toggling.value.has(cita.id)) return
    toggling.value.add(cita.id)
    try {
        const updated = await miAgendaApi.update(cita.id, { realizada: !cita.realizada })
        if (resultado.value) {
            const idx = resultado.value.items.findIndex((i) => i.id === cita.id)
            if (idx !== -1) resultado.value.items[idx] = updated
        }
    } finally {
        toggling.value.delete(cita.id)
    }
}

function fechaISO(d: Date): string {
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
}

function hoyISO(): string {
    return fechaISO(new Date())
}

function sumarDias(fecha: string, dias: number): string {
    const d = new Date(fecha + 'T00:00:00')
    d.setDate(d.getDate() + dias)
    return fechaISO(d)
}

const fechaActual = computed<string>(() => (route.query.fecha as string) || hoyISO())

const esHoy = computed(() => fechaActual.value === hoyISO())

const fechaFormateada = computed(() => {
    const d = new Date(fechaActual.value + 'T00:00:00')
    return d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
})

function irAFecha(fecha: string) {
    router.push({ query: { fecha } })
}

function irAHoy() {
    router.push({ query: {} })
}

function diaAnterior() {
    irAFecha(sumarDias(fechaActual.value, -1))
}

function diaSiguiente() {
    irAFecha(sumarDias(fechaActual.value, 1))
}

function cambiarPagina(pagina: number) {
    router.push({ query: { ...route.query, page: String(pagina) } })
}

function formatHora(fecha: string): string {
    return new Date(fecha).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
}

async function cargar() {
    const pagina = Number(route.query.page) || 1
    loading.value = true
    error.value = null
    try {
        resultado.value = await miAgendaApi.getAll({
            fecha: fechaActual.value,
            page: pagina,
            pageSize: PAGE_SIZE,
        })
    } catch {
        error.value = 'No se pudieron cargar las citas.'
    } finally {
        loading.value = false
    }
}

watch(
    () => route.query,
    () => cargar(),
    { immediate: true },
)
</script>
